import { useQuery } from "@tanstack/react-query"
import { Search, X } from "lucide-react"
import { useEffect, useId, useState } from "react"

import { api } from "@/api/cliente"
import { NOMBRE_TIPO, type Pagador, type TipoPagador } from "@/componentes/ingresos/tipos"
import { Boton } from "@/componentes/ui/Boton"

const TIPOS: TipoPagador[] = ["CLIENTE", "PROVEEDOR", "EMPLEADO"]

/**
 * Quién mandó el dinero: un cliente, un proveedor o un empleado, buscado por nombre o
 * por RIF o cédula.
 *
 * ⚠ **«Por determinar» es una respuesta válida** (pedido del usuario): el ingreso se
 * registra en el banco al momento y el pagador se le pone después, desde la lista.
 *
 * ⚠ **La búsqueda es del servidor**, con 300 ms de espera, y en las tres listas a la vez
 * si no se elige tipo: quien teclea un RIF no sabe si está como cliente o como proveedor.
 */
export function SelectorPagador({
  valor,
  onCambiar,
  porDeterminar = true,
}: {
  valor: Pagador | null
  onCambiar: (pagador: Pagador | null) => void
  /** Si se ofrece dejarlo por determinar. Al asignar un pagador después, no. */
  porDeterminar?: boolean
}) {
  const id = useId()
  const [tipo, setTipo] = useState<TipoPagador | null>(null)
  const [texto, setTexto] = useState("")
  const [buscar, setBuscar] = useState("")

  useEffect(() => {
    const espera = window.setTimeout(() => setBuscar(texto.trim()), 300)
    return () => window.clearTimeout(espera)
  }, [texto])

  const resultados = useQuery({
    queryKey: ["pagadores", buscar, tipo],
    queryFn: () => {
      const q = new URLSearchParams({ buscar })
      if (tipo) q.set("tipo", tipo)
      return api.get<Pagador[]>(`/api/v1/ingresos/pagadores?${q}`)
    },
    enabled: buscar.length >= 2 && valor === null,
  })

  if (valor !== null) {
    return (
      <div className="tarjeta flex items-center justify-between gap-3 p-3 text-sm">
        <div className="min-w-0">
          <p className="text-xs uppercase" style={{ color: "var(--fg-muted)" }}>
            {NOMBRE_TIPO[valor.tipo]}
          </p>
          <p className="truncate font-medium">{valor.nombre}</p>
          {valor.documento && (
            <p className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
              {valor.documento}
            </p>
          )}
        </div>
        <Boton tamano="sm" onClick={() => onCambiar(null)}>
          <X size={14} /> Cambiar
        </Boton>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <fieldset className="flex flex-wrap gap-2">
        <legend className="sr-only">Tipo de pagador</legend>
        {TIPOS.map((t) => (
          <Boton
            key={t}
            tamano="sm"
            variante={tipo === t ? "primario" : undefined}
            aria-pressed={tipo === t}
            onClick={() => setTipo(tipo === t ? null : t)}
          >
            {NOMBRE_TIPO[t]}
          </Boton>
        ))}
      </fieldset>
      <label className="sr-only" htmlFor={id}>
        Buscar pagador
      </label>
      <div className="relative">
        <Search
          size={16}
          aria-hidden={true}
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
          style={{ color: "var(--fg-muted)" }}
        />
        <input
          id={id}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Nombre, RIF o cédula…"
          className="campo pl-9"
          autoComplete="off"
        />
      </div>
      {buscar.length >= 2 && (
        <ul className="tarjeta max-h-56 divide-y overflow-y-auto text-sm">
          {resultados.isPending && <li className="p-3">Buscando…</li>}
          {resultados.data?.length === 0 && (
            <li className="p-3" style={{ color: "var(--fg-muted)" }}>
              Nadie con ese nombre o documento.
            </li>
          )}
          {resultados.data?.map((p) => (
            <li key={`${p.tipo}-${p.id}`}>
              <button
                type="button"
                className="foco-cristal toque-cristal flex w-full items-center justify-between gap-3 p-3 text-left"
                onClick={() => onCambiar(p)}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{p.nombre}</span>
                  <span className="block text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                    {[NOMBRE_TIPO[p.tipo], p.documento].filter(Boolean).join(" · ")}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {porDeterminar && (
        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          Si todavía no se sabe quién pagó, déjalo vacío: el ingreso queda por determinar y se le
          asigna después.
        </p>
      )}
    </div>
  )
}
